import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { Zap, KeyRound, CheckCircle2 } from "lucide-react";
import { formatApiError } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await axios.post(`${BACKEND}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center p-6">
      <Toaster theme="dark" richColors position="top-right" />
      <Card className="w-full max-w-md p-8 rounded-sm bg-card border-border">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-8 w-8 rounded-sm bg-primary flex items-center justify-center"><Zap className="h-4 w-4 text-white" /></div>
          <div className="font-display font-extrabold">LeadifyCRM</div>
        </div>
        {!token ? (
          <div className="text-center py-4">
            <div className="font-display text-2xl font-extrabold tracking-tight">Missing token</div>
            <p className="text-sm text-muted-foreground mt-2">This link looks invalid. <Link to="/forgot-password" className="text-primary hover:underline">Request a new one</Link>.</p>
          </div>
        ) : done ? (
          <div className="text-center py-4">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-3" />
            <div className="font-display text-2xl font-extrabold tracking-tight">Password updated.</div>
            <p className="text-sm text-muted-foreground mt-2">Redirecting to sign in…</p>
          </div>
        ) : (
          <>
            <KeyRound className="h-6 w-6 text-primary mb-2" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-2">New password</div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight mb-6">Choose a new password.</h2>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>New password</Label><Input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5 rounded-sm bg-secondary/40" data-testid="reset-password-input" /></div>
              <div><Label>Confirm password</Label><Input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1.5 rounded-sm bg-secondary/40" data-testid="reset-password-confirm" /></div>
              <Button type="submit" disabled={loading} className="w-full rounded-sm bg-primary hover:bg-primary/90 h-11" data-testid="reset-submit-button">{loading ? "…" : "Update password"}</Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
